import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.svg';
import wesamLogo from '../assets/wesamlogo.svg';
import '../App.css';

const NAV_LINKS = [
  { to: '/aboutMe', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/manifesto', label: 'Manifesto' },
  { to: '/contact', label: 'Contact' },
];

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function NavBar() {
  const [expanded, setExpanded] = useState(false);
  const headerRef = useRef(null);
  const toggleRef = useRef(null);
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const collapseNav = useCallback(() => {
    setExpanded(false);
  }, []);

  const toggleNav = useCallback(() => {
    setExpanded((open) => !open);
  }, []);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (!expanded) return undefined;

    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  // Close on Escape; trap focus inside the open mobile menu
  useEffect(() => {
    if (!expanded) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const getFocusable = () => {
      const root = headerRef.current;
      if (!root) return [];
      return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => {
          if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') {
            return false;
          }
          const style = window.getComputedStyle(el);
          return style.visibility !== 'hidden' && style.display !== 'none';
        }
      );
    };

    const focusables = getFocusable();
    const first = focusables[0];
    if (first) first.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setExpanded(false);
        return;
      }

      if (event.key !== 'Tab') return;

      const items = getFocusable();
      if (items.length === 0) return;

      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const restoreTarget = previouslyFocusedRef.current || toggleRef.current;
      if (restoreTarget && typeof restoreTarget.focus === 'function') {
        restoreTarget.focus();
      }
    };
  }, [expanded]);

  // Close when clicking / tapping outside the header
  useEffect(() => {
    if (!expanded) return undefined;

    const onPointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [expanded]);

  // Collapse when resizing to desktop so state stays in sync with layout
  useEffect(() => {
    const media = window.matchMedia('(min-width: 992px)');

    const onChange = (event) => {
      if (event.matches) setExpanded(false);
    };

    if (media.addEventListener) {
      media.addEventListener('change', onChange);
    } else {
      media.addListener(onChange);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', onChange);
      } else {
        media.removeListener(onChange);
      }
    };
  }, []);

  return (
    <header
      ref={headerRef}
      className={`site-header${expanded ? ' is-open' : ''}`}
    >
      <nav className="site-nav" aria-label="Primary">
        <Link
          to="/"
          className="site-nav__brand"
          onClick={() => {
            collapseNav();
            window.scrollTo(0, 0);
          }}
        >
          <img
            alt="Wesam Technologies logo"
            src={logo}
            width="60"
            height="60"
            className="App-logo"
          />
          <img
            alt="Wesam"
            src={wesamLogo}
            width="75"
            height="55"
            className="site-nav__wordmark"
          />
        </Link>

        <div
          className="site-nav__backdrop"
          aria-hidden="true"
          onClick={collapseNav}
        />

        <div
          ref={panelRef}
          id="primary-navigation"
          className="site-nav__panel"
        >
          <ul className="site-nav__list">
            {NAV_LINKS.map(({ to, label }, index) => (
              <li
                key={to}
                className="site-nav__item"
                style={{ '--nav-item-index': index }}
              >
                <Link
                  to={to}
                  className="site-nav__link nav-link"
                  onClick={collapseNav}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="site-nav__end">
          <div className="site-nav__tools" aria-label="Site tools">
            <div id="music-nav-slot" className="site-nav__music" />
            <div id="chat-nav-slot" className="site-nav__chat" />
            <div id="telemetry-nav-slot" className="site-nav__telemetry" />
          </div>
          <button
            ref={toggleRef}
            type="button"
            className="site-nav__toggle"
            aria-expanded={expanded}
            aria-controls="primary-navigation"
            aria-label={expanded ? 'Close navigation' : 'Open navigation'}
            onClick={toggleNav}
          >
            <span className="site-nav__burger" aria-hidden="true">
              <span className="site-nav__burger-line" />
              <span className="site-nav__burger-line" />
              <span className="site-nav__burger-line" />
            </span>
          </button>
        </div>
      </nav>
    </header>
  );
}

export default NavBar;
